/**
 * The maths symbols a learner cannot type on a keyboard or a phone (#660; founder: "users cant write math symbols, so when
 * you ask me to write it myself, i cant"). One real button per symbol, named in words ("Insert element of"), 44px square.
 *
 * It only reports which symbol was tapped; the field that owns the caret does the inserting (`reading-editor.tsx`), so a
 * symbol lands where the caret is, not at the end. A press never takes focus from the field: the mouse-down is cancelled,
 * so the on-screen keyboard stays up on a phone and the caret stays where it was.
 *
 * Symbols are the board's, plus four common in linear algebra and calculus. Nothing here is a reading or a judgement.
 */

import { useHomeworkLabels } from "../../i18n/homework-labels.js";

export const SYMBOLS: readonly string[] = [
  "ℝ",
  "ℕ",
  "ℤ",
  "∈",
  "∉",
  "≤",
  "≥",
  "≠",
  "→",
  "⇒",
  "∀",
  "∃",
  "λ",
  "π",
  "Σ",
  "∫",
  "√",
  "²",
  "⁻¹",
  "∞",
  "⊂",
  "∪",
  "∩",
  // Four more, common in linear algebra and calculus.
  "≈",
  "⊥",
  "∂",
  "θ",
];

export function SymbolPalette({
  onInsert,
  disabled = false,
}: {
  readonly onInsert: (symbol: string) => void;
  readonly disabled?: boolean;
}): React.JSX.Element {
  const t = useHomeworkLabels();
  return (
    <div
      className="symbol-palette"
      role="group"
      aria-label={t.symbolsGroup}
      dir="ltr"
    >
      {SYMBOLS.map((symbol) => (
        <button
          key={symbol}
          type="button"
          className="symbol-tile"
          disabled={disabled}
          aria-label={t.insertSymbol(t.symbolName(symbol))}
          // Keep the caret and the keyboard in the field.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onInsert(symbol)}
        >
          {symbol}
        </button>
      ))}
    </div>
  );
}
